import { drawArt } from "./ui-art";

/** Resolve font hinting and art resampling to the same pixel grid as the HUD.
 * TextMetrics supplies the initial ink centre; this cached raster refinement
 * keeps the final dark outline centred even at fractional viewport sizes.
 */
export function ribbonInkOffset(
  source: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  label: string,
  tx: number,
  ty: number,
  width: number,
  dpr: number,
): { x: number; y: number } {
  const canvas = source.canvas.ownerDocument?.createElement("canvas");
  if (!canvas) return { x: 0, y: 0 };
  canvas.dataset.qaIgnoreText = "true";
  const origin = Math.floor(rect.y * dpr) / dpr;
  canvas.width = Math.ceil(width * dpr);
  canvas.height = Math.ceil((rect.y - origin + rect.height) * dpr) + 2;
  const c = canvas.getContext("2d", { willReadFrequently: true });
  if (!c) return { x: 0, y: 0 };
  c.scale(dpr, dpr);
  drawArt(
    c,
    "badge_ribbon_blank",
    rect.x,
    rect.y - origin,
    rect.width,
    rect.height,
  );
  let data = c.getImageData(0, 0, canvas.width, canvas.height).data;
  const color = (x: number, y: number) => {
    const i = (y * canvas.width + x) * 4;
    return [data[i]!, data[i + 1]!, data[i + 2]!];
  };
  const blue = (x: number, y: number) => {
    const [r, g, b] = color(x, y);
    return b! > 200 && r! < 90 && g! > 70 && g! < 150;
  };
  const x0 = Math.floor(width * dpr * 0.15);
  let top = 0;
  while (top < canvas.height && !blue(x0, top)) top++;
  if (top === canvas.height) return { x: 0, y: 0 };
  let bottom = top;
  while (bottom + 1 < canvas.height && blue(x0, bottom + 1)) bottom++;
  const mid = Math.floor((top + bottom) / 2);
  let left = x0,
    right = canvas.width - 1 - x0;
  while (left > 0 && blue(left - 1, mid)) left--;
  while (right + 1 < canvas.width && blue(right + 1, mid)) right++;
  c.font = source.font;
  c.textAlign = "center";
  c.textBaseline = "alphabetic";
  c.strokeStyle = "#2A2440";
  c.lineWidth = 2.6;
  c.lineJoin = "round";
  c.strokeText(label, tx, ty - origin);
  c.fillStyle = "#FFFFFF";
  c.fillText(label, tx, ty - origin);
  data = c.getImageData(0, 0, canvas.width, canvas.height).data;
  let l = Infinity,
    r = -Infinity,
    t = Infinity,
    b = -Infinity;
  for (let y = top; y <= bottom; y++)
    for (let x = left; x <= right; x++) {
      const [R, G, B] = color(x, y);
      if (
        data[(y * canvas.width + x) * 4 + 3]! >= 250 &&
        R! < 110 &&
        G! < 100 &&
        B! < 140 &&
        B! - R! < 90
      ) {
        l = Math.min(l, x);
        r = Math.max(r, x);
        t = Math.min(t, y);
        b = Math.max(b, y);
      }
    }
  return Number.isFinite(l)
    ? {
        x: Math.round((left + right - l - r) / 2) / dpr,
        y: Math.round((top + bottom - t - b) / 2) / dpr,
      }
    : { x: 0, y: 0 };
}
